import PropTypes from 'prop-types';
import React from 'react';
import TrustCard from '_components/atomic/TrustCard';
import Icon from '_components/atomic/Icon';
import { ICONS_ALIASES } from '_constants/icons';
import { trustPilots } from '_constants/trustPilots';
import styled from 'styled-components';
import { theme } from '_constants/theme';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import SwiperCore, { Navigation } from 'swiper';
import { useSwiperRef } from '_hooks';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();
const { colors, devices, fontSizes, spaces } = theme;
SwiperCore.use([Navigation]);

const StyledTrustPilotSection = styled.div`
  max-width: ${spaces.large24X * 10 + 80}px;
  display: flex;
  gap: 30px;
  .swiper {
    display: flex;
    flex-direction: column;
  }
  .swiper-slide {
    width: 31.5%;
  }
  @media ${devices.laptopM} {
    padding-left: ${spaces.large2X}px;
    padding-right: ${spaces.large2X}px;
  }
  @media ${devices.tablet} {
    margin-left: ${spaces.regular}px;
    margin-right: ${spaces.regular}px;
    .swiper-slide {
      width: 100%;
    }
  }
`;
const StyledTrustCard = styled.div`
  height: 100%;
`;
const StyledSliderNavigationButtons = styled.div`
  height: 100%;
  align-items: end;
  display: flex;
  justify-content: center;
  gap: ${spaces.large2X}px;
  .navButton {
    cursor: pointer;
  }
  .swiper-button-disabled {
    cursor: initial;
    svg {
      path {
        fill: ${colors.gray[500]};
      }
    }
  }
  @media ${devices.tablet} {
    padding-right: 0px;
    svg {
      width: 24px;
    }
  }
`;
const StyledSliderNavigation = styled.div`
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  margin-top: ${spaces.large7X - 2}px;
  margin-left: 9%;
  margin-right: 9%;
  @media ${devices.tablet} {
    flex-direction: column;
    align-items: center;
    gap: ${spaces.large2X - 2}px;
    margin-top: ${spaces.large2X}px;
    margin-left: ${spaces.regular}px;
    margin-right: ${spaces.regular}px;
  }
`;
const StyledTrustLogoSection = styled.div`
  flex-direction: column;
  .trustLogoReviews {
    font-family: 'Aeonik';
    font-style: normal;
    font-weight: 400;
    font-size: ${fontSizes.regular}px;
    line-height: 150%;
    color: ${colors.gray[500]};
  }
  @media ${devices.tablet} {
    align-items: center;
    display: flex;
    .trustLogoReviews {
      text-align: center;
      padding-left: ${spaces.large2X - 2}px;
      padding-right: ${spaces.large2X - 2}px;
    }
  }
`;

const StyledSectionWithHeader = styled.div`
  margin-top: ${spaces.large24X}px;
  display: flex;
  flex-direction: column;
  .headerSection {
    font-family: 'Aeonik';
    font-style: normal;
    font-weight: 500;
    font-size: ${fontSizes.large11X}px;
    line-height: 130%;
    text-align: center;
    color: ${colors.gray[800]};
    flex: none;
    order: 0;
    align-self: stretch;
    flex-grow: 0;
    margin-left: 9%;
    margin-right: 9%;
  }
  .subHeaderSection {
    font-family: 'Aeonik';
    font-style: normal;
    font-weight: 400;
    font-size: ${fontSizes.large}px;
    line-height: 150%;
    margin-top: ${spaces.medium}px;
    margin-bottom: ${spaces.large6X + 2}px;
    text-align: center;
    color: ${colors.gray[700]};
    flex: none;
    order: 0;
    align-self: stretch;
    flex-grow: 0;
    margin-left: 9%;
    margin-right: 9%;
  }
  @media ${devices.tablet} {
    margin-top: ${spaces.large9X}px;
    margin-bottom: ${spaces.large9X}px;
    margin-left: 0;
    margin-right: 0;
    .headerSection {
      font-size: ${fontSizes.large2X}px;
      line-height: 150%;
      margin-left: ${spaces.regular}px;
      margin-right: ${spaces.regular}px;
    }
    .subHeaderSection {
      margin-left: ${spaces.regular}px;
      margin-right: ${spaces.regular}px;
      font-size: ${fontSizes.regular}px;
      line-height: 150%;
      margin-top: ${spaces.smallX}px;
      margin-bottom: ${spaces.large2X}px;
    }
  }
`;
const TrustPilotSection = ({ title, subTitle, elRef }) => {
  const [nextEl, nextElRef] = useSwiperRef();
  const [prevEl, prevElRef] = useSwiperRef();
  return (
    <StyledSectionWithHeader ref={elRef}>
      {title && <div className="headerSection">{title}</div>}
      {subTitle && <div className="subHeaderSection">{subTitle}</div>}
      <StyledTrustPilotSection>
        <Swiper
          spaceBetween={32}
          slidesPerView="auto"
          navigation={{
            prevEl,
            nextEl,
          }}
        >
          {trustPilots.map((trustPilot, idx) => {
            const { author, description } = trustPilot;
            return (
              <SwiperSlide key={`step:${idx}`}>
                <StyledTrustCard>
                  <TrustCard author={author} description={description} />
                </StyledTrustCard>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </StyledTrustPilotSection>
      <StyledSliderNavigation>
        <StyledTrustLogoSection>
          <div>
            <picture>
              <source
                srcSet={`${publicRuntimeConfig.basePath}/images/trustpilot-logo.svg`}
                type="image/png"
              />
              <img
                src={`${publicRuntimeConfig.basePath}/images/trustpilot-logo.svg`}
                alt=""
              />
            </picture>
          </div>
          <div style={{ marginTop: '14px', marginBottom: '5px' }}>
            <picture>
              <source
                srcSet={`${publicRuntimeConfig.basePath}/images/5-star.svg`}
                type="image/png"
              />
              <img
                src={`${publicRuntimeConfig.basePath}/images/5-star.svg`}
                alt=""
              />
            </picture>
          </div>
          <div className="trustLogoReviews">
            4.7 out of 5 based on 55012 reviews. Showing our 4 and 5 stars
            reviews.
          </div>
        </StyledTrustLogoSection>
        <StyledSliderNavigationButtons>
          <div ref={prevElRef} className="navButton">
            <Icon
              alias={ICONS_ALIASES.arrowLeft}
              color={colors.blue[500]}
              height={30}
              width={30}
            />
          </div>
          <div ref={nextElRef} className="navButton">
            <Icon
              alias={ICONS_ALIASES.arrowRight}
              color={colors.blue[500]}
              height={30}
              width={30}
            />
          </div>
        </StyledSliderNavigationButtons>
      </StyledSliderNavigation>
    </StyledSectionWithHeader>
  );
};

TrustPilotSection.propTypes = {
  title: PropTypes.node,
  subTitle: PropTypes.node,
};

export default TrustPilotSection;
